import { Input, Select } from "antd";
import React, { useRef, useState } from "react";
import { parsePhoneNumberFromString } from "libphonenumber-js/max";
import { activeCountries, dialCodeOptions, knownDialCodes } from "../../../config/countries";
import { checkPhone } from "../../../utils/phoneValidation";

// Phone country code control for the subscriber forms. Lists the dial codes
// of the active E-Paper Settings countries plus "Others / Enter Code", which
// shows a box to type any international code. The form value is always the
// code itself ("+44"), so submit and phone validation are unchanged.

const OTHER = "__other__";

// "44", "+44", "+ 4 4" → "+44" (at most 4 digits); "" when empty.
const normalizeDialCode = (value: unknown): string => {
  const digits = String(value ?? "").replace(/\D/g, "").slice(0, 4);
  return digits ? `+${digits}` : "";
};

// Form rule for a field using this control. Same message as the Add Agent
// form; an unknown code is reported on the number (see phoneNumberRule).
export const dialCodeRule = {
  validator(_: unknown, value?: string) {
    if (!value || !/^\+\d{1,4}$/.test(value)) {
      return Promise.reject(new Error("Please enter the country code, e.g. +880"));
    }
    return Promise.resolve();
  },
};

// Number field: digits only (spaces, dashes and brackets dropped as typed),
// at most 15 — as on the Add Agent form.
export const normalizePhoneDigits = (value: unknown): string =>
  String(value ?? "").replace(/\D/g, "").slice(0, 15);

// The Add Agent check for a local number and calling code. Messages name the
// E-Paper Settings country using the code ("India (+91)"), or just the code
// for any other one. A code of an active E-Paper Settings country is
// "configured": accepted even if libphonenumber doesn't know it (custom code).
export const validateDialPhone = (dialCode?: string, local?: string) => {
  const country = dialCode ? activeCountries().find((c) => c.dialCode === dialCode) : undefined;
  const where = country ? `${country.name} (${dialCode})` : dialCode || "";
  return checkPhone(dialCode, local, where, country?.code, !!country);
};

// Form rule for the number next to this control (code field "countryCode";
// give the number field dependencies={["countryCode"]}). `isUnchanged` lets
// an edit form keep a saved number (including legacy ones) without checking.
export const phoneNumberRule =
  (isUnchanged?: (dialCode: string | undefined, local: string) => boolean) =>
  ({ getFieldValue }: { getFieldValue: (name: string) => any }) => ({
    validator(_: unknown, value: unknown) {
      const local = String(value ?? "").trim();
      const dialCode: string | undefined = getFieldValue("countryCode");
      if (local && isUnchanged?.(dialCode, local)) return Promise.resolve();
      const result = validateDialPhone(dialCode, local);
      if (result.valid || !result.error) return Promise.resolve();
      return Promise.reject(new Error(result.error));
    },
  });

// Splits a saved phone ("+971501234567") into calling code + local number:
// a configured or legacy code first (longest match), else any international
// code libphonenumber recognises (shown under Others). Null when neither fits.
export const splitSavedPhone = (
  phone: string
): { countryCode: string; phoneNumber: string } | null => {
  for (const code of knownDialCodes()) {
    if (phone.startsWith(code)) {
      return { countryCode: code, phoneNumber: phone.slice(code.length) };
    }
  }
  const parsed = phone.startsWith("+") ? parsePhoneNumberFromString(phone) : undefined;
  if (parsed && phone === `+${parsed.countryCallingCode}${parsed.nationalNumber}`) {
    return { countryCode: `+${parsed.countryCallingCode}`, phoneNumber: parsed.nationalNumber };
  }
  return null;
};

type Props = {
  value?: string;
  onChange?: (value: string) => void;
  size?: "small" | "middle" | "large";
};

const DialCodeInput = ({ value, onChange, size }: Props) => {
  const options = dialCodeOptions();
  const listed = !!value && options.some((o) => o.code === value);
  // Set when Others is picked, so the box stays open while a code that
  // happens to be listed is typed. A code set from outside (the form
  // loading a record, or the Country field) clears it.
  const [custom, setCustom] = useState(false);
  const emitted = useRef<string | undefined>(value);
  if (custom && value !== emitted.current) setCustom(false);
  const isOther = custom || (!!value && !listed);

  const emit = (next: string) => {
    emitted.current = next;
    onChange?.(next);
  };

  return (
    <>
      <Select
        size={size}
        showSearch
        optionFilterProp="label"
        style={{ width: isOther ? "25%" : "40%", minWidth: 110 }}
        value={isOther ? OTHER : value}
        onChange={(v: string) => {
          setCustom(v === OTHER);
          emit(v === OTHER ? "" : v);
        }}
        options={[
          ...options.map((o) => ({ value: o.code, label: o.label })),
          { value: OTHER, label: "Others / Enter Code" },
        ]}
      />
      {isOther && (
        <Input
          size={size}
          inputMode="tel"
          placeholder="+code"
          style={{ width: "15%", minWidth: 70 }}
          value={value}
          onChange={(e) => emit(normalizeDialCode(e.target.value))}
        />
      )}
    </>
  );
};

export default DialCodeInput;
